// src/components/QuestionTypes/JournalEntryQuestion.js
// 目的：仕訳入力型の練習問題UI。回答チェックと進捗(progress)の更新を行う。
// 修正点：未使用の App を削除して ESLint 警告を解消。

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Select,
  InputNumber,
  Table,
  Button,
  Form,
  Alert,
  Divider,
  Space,
  Image,
} from 'antd';
import { CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import './JournalEntryQuestion.css';

// Firebase と認証情報
import { db } from '../../firebase-config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const { Paragraph, Text } = Typography;
const { Option } = Select;

// ルビ対応テキスト
const RubyText = ({ parts }) => (
  <Paragraph className="question-content">
    {Array.isArray(parts) && parts.map((part, index) =>
      part.type === 'ruby' ? (
        <ruby key={index}>{part.text}<rt>{part.ruby}</rt></ruby>
      ) : (
        <span key={index}>{part.value}</span>
      )
    )}
  </Paragraph>
);

// 解説（テキスト/画像/スタイル付き）の描画
const ExplanationText = ({ content }) => {
  if (!content) return null;
  if (typeof content === 'string' && /\.(jpeg|jpg|gif|png|svg)$/.test(content)) {
    return <Image src={content} alt="Hint" preview={false} />;
  }
  if (typeof content === 'string') {
    return <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{content}</Paragraph>;
  }
  if (typeof content === 'object' && Array.isArray(content.parts)) {
    return (
      <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
        {content.parts.map((part, index) => {
          if (part.type === 'styled') {
            const style = { color: part.color || 'inherit', fontWeight: part.bold ? 'bold' : 'normal' };
            return <span key={index} style={style}>{part.value}</span>;
          }
          return <span key={index}>{part.value}</span>;
        })}
      </Paragraph>
    );
  }
  if (content.type === 'image' && content.url) {
    return <Image src={content.url} alt="Hint" preview={false} />;
  }
  return null;
};

const JournalEntryQuestion = ({ question, onNextQuestion, exerciseId, levelId, chapterId }) => {
  const [form] = Form.useForm();
  const [submissionState, setSubmissionState] = useState('notSubmitted'); // notSubmitted | incorrect | correct
  const [incorrectAttempts, setIncorrectAttempts] = useState(0);
  const [submittedAnswer, setSubmittedAnswer] = useState(null);
  const { currentUser } = useAuth();

  // 行数は正解データの行数に合わせる
  const initialRows = Array.from({ length: question.answer.length }, () => ({}));

  // 問題切り替え時に初期化
  useEffect(() => {
    form.resetFields();
    setSubmissionState('notSubmitted');
    setIncorrectAttempts(0);
    setSubmittedAnswer(null);
  }, [exerciseId, form]);

  // 回答チェック（借方/貸方の合計一致＆勘定科目ごとの金額一致）
  const checkAnswer = (userEntries) => {
    const correctAnswer = question.answer;
    const finalUserEntries = userEntries || [];

    const buildAccountMap = (entries) => {
      const map = new Map();
      (entries || []).forEach(entry => {
        if (!entry) return;
        if (entry.debitAccount && entry.debitAmount) {
          const current = map.get(entry.debitAccount) || { debit: 0, credit: 0 };
          map.set(entry.debitAccount, { ...current, debit: current.debit + entry.debitAmount });
        }
        if (entry.creditAccount && entry.creditAmount) {
          const current = map.get(entry.creditAccount) || { debit: 0, credit: 0 };
          map.set(entry.creditAccount, { ...current, credit: current.credit + entry.creditAmount });
        }
      });
      return map;
    };

    const userMap = buildAccountMap(finalUserEntries);
    const correctMap = buildAccountMap(correctAnswer);

    // 貸借一致 & 0でない
    let userTotalDebit = 0; let userTotalCredit = 0;
    userMap.forEach(value => { userTotalDebit += value.debit; userTotalCredit += value.credit; });
    if (userTotalDebit !== userTotalCredit || userTotalDebit === 0) {
      return { isCorrect: false, reason: 'amount' };
    }

    // 科目セット一致
    const userAccounts = Array.from(userMap.keys()).sort();
    const correctAccounts = Array.from(correctMap.keys()).sort();
    if (JSON.stringify(userAccounts) !== JSON.stringify(correctAccounts)) {
      return { isCorrect: false, reason: 'account' };
    }

    // 各科目の金額一致
    for (const account of correctAccounts) {
      const u = userMap.get(account);
      const c = correctMap.get(account);
      if (u.debit !== c.debit || u.credit !== c.credit) {
        return { isCorrect: false, reason: 'amount' };
      }
    }
    return { isCorrect: true, reason: null };
  };

  // progress を更新
  const updateProgress = async (isCorrect) => {
    if (!currentUser || !currentUser.id) return;

    const progressDocId = `${currentUser.id}_${exerciseId}`;
    const progressDocRef = doc(db, 'progress', progressDocId);

    try {
      const snap = await getDoc(progressDocRef);
      if (snap.exists()) {
        await updateDoc(progressDocRef, {
          status: isCorrect ? 'completed' : snap.data().status,
          attempts: increment(1),
          completedCount: increment(isCorrect ? 1 : 0),
          lastAttempted: serverTimestamp(),
        });
      } else {
        await setDoc(progressDocRef, {
          userId: currentUser.id,
          exerciseId,
          levelId,
          chapterId,
          status: isCorrect ? 'completed' : 'incomplete',
          attempts: 1,
          completedCount: isCorrect ? 1 : 0,
          lastAttempted: serverTimestamp(),
        });
      }
    } catch (e) {
      console.error('進捗更新エラー:', e);
    }
  };

  // 送信時処理
  const onFinish = async (values) => {
    setSubmittedAnswer(values.entries || []);
    const result = checkAnswer(values.entries);
    await updateProgress(result.isCorrect);

    if (result.isCorrect) {
      setSubmissionState('correct');
      setIncorrectAttempts(0);
    } else {
      setSubmissionState('incorrect');
      setIncorrectAttempts(prev => prev + 1);
    }
  };

  // 再入力
  const handleTryAgain = () => {
    form.resetFields();
    setSubmissionState('notSubmitted');
    setSubmittedAnswer(null);
  };

  // ヒント（誤答回数で段階的に出し分け）
  const getHintText = () => {
    const hintsForError = question.hints?.account;
    if (!hintsForError) return '答えが正しくありません。もう一度ご確認ください。';
    const idx = incorrectAttempts <= 2 ? 0 : 1;
    return hintsForError[Math.min(idx, hintsForError.length - 1)];
  };

  // 解答表示用テーブル列
  const resultColumns = [
    { title: '借り方科目', dataIndex: 'debitAccount', key: 'debitAccount' },
    {
      title: '金額',
      dataIndex: 'debitAmount',
      key: 'debitAmount',
      render: (val) => (val != null ? new Intl.NumberFormat('ja-JP').format(val) : ''),
    },
    { title: '貸方科目', dataIndex: 'creditAccount', key: 'creditAccount' },
    {
      title: '金額',
      dataIndex: 'creditAmount',
      key: 'creditAmount',
      render: (val) => (val != null ? new Intl.NumberFormat('ja-JP').format(val) : ''),
    },
  ];

  return (
    <div>
      <RubyText parts={question.content.parts} />

      <Form form={form} name="journal_entry" onFinish={onFinish} autoComplete="off">
        {submissionState === 'notSubmitted' && (
          <Form.List name="entries" initialValue={initialRows}>
            {(fields) => (
              <Table
                rowKey={(field) => field.key}
                columns={[
                  {
                    title: '借方科目',
                    width: '25%',
                    render: (field) => (
                      <Form.Item name={[field.name, 'debitAccount']} noStyle>
                        <Select placeholder="勘定科目を選択" style={{ width: '100%' }}>
                          {Array.isArray(question.table.options) &&
                            question.table.options.map((opt) => (
                              <Option key={opt} value={opt}>{opt}</Option>
                            ))}
                        </Select>
                      </Form.Item>
                    ),
                  },
                  {
                    title: '金額',
                    width: '25%',
                    render: (field) => (
                      <Form.Item name={[field.name, 'debitAmount']} noStyle>
                        <InputNumber
                          placeholder="金額を入力"
                          style={{ width: '100%' }}
                          formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(v) => v.replace(/\$\s?|(,*)/g, '').replace(/[^0-9]/g, '')}
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: '貸方科目',
                    width: '25%',
                    render: (field) => (
                      <Form.Item name={[field.name, 'creditAccount']} noStyle>
                        <Select placeholder="勘定科目を選択" style={{ width: '100%' }}>
                          {Array.isArray(question.table.options) &&
                            question.table.options.map((opt) => (
                              <Option key={opt} value={opt}>{opt}</Option>
                            ))}
                        </Select>
                      </Form.Item>
                    ),
                  },
                  {
                    title: '金額',
                    width: '25%',
                    render: (field) => (
                      <Form.Item name={[field.name, 'creditAmount']} noStyle>
                        <InputNumber
                          placeholder="金額を入力"
                          style={{ width: '100%' }}
                          formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(v) => v.replace(/\$\s?|(,*)/g, '').replace(/[^0-9]/g, '')}
                        />
                      </Form.Item>
                    ),
                  },
                ]}
                dataSource={fields}
                pagination={false}
                bordered
              />
            )}
          </Form.List>
        )}

        {submissionState === 'incorrect' && (
          <Alert
            message={<Text strong>不正解！ (回数: {incorrectAttempts})</Text>}
            description={
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>あなたの回答:</Text>
                <Table
                  columns={resultColumns}
                  dataSource={submittedAnswer.map((ans, i) => ({ ...ans, key: i }))}
                  pagination={false}
                  bordered
                  size="small"
                  style={{ background: '#fffbe6' }}
                />
                <Divider style={{ margin: '8px 0' }} />
                <Text strong>ヒント:</Text>
                <ExplanationText content={getHintText()} />
              </Space>
            }
            type="error"
            showIcon
            action={<Button icon={<ReloadOutlined />} onClick={handleTryAgain}>再入力</Button>}
          />
        )}

        {submissionState === 'correct' && (
          <Alert
            message={<Text strong>正解！</Text>}
            description={
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>正しい答えは:</Text>
                <Table
                  columns={resultColumns}
                  dataSource={question.answer.map((ans, i) => ({ ...ans, key: i }))}
                  pagination={false}
                  bordered
                  size="small"
                />
                <Divider style={{ margin: '8px 0' }} />
                <Text strong>解説:</Text>
                <ExplanationText content={question.explanation_correct} />
              </Space>
            }
            type="success"
            showIcon
            action={<Button type="primary" onClick={onNextQuestion}>次の問題</Button>}
          />
        )}

        {submissionState === 'notSubmitted' && (
          <Form.Item style={{ marginTop: '24px', textAlign: 'right' }}>
            <Button type="primary" htmlType="submit" icon={<CheckCircleOutlined />}>
              答え合わせ
            </Button>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default JournalEntryQuestion;
